import React from "react";
import {
  Drawer,
  Descriptions,
  Table,
  Tag,
  Card,
  Statistic,
  Row,
  Col,
  Divider,
  Empty,
  Spin,
} from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import FinanceService from "../../../dependencies/custom/financeService";

/**
 * Transport Fee Detail Drawer
 * Shows detailed information about a transport fee assignment
 */
const TransportFeeDetailDrawer = ({
  visible,
  onClose,
  transportFeeData,
  loading = false,
}) => {
  if (!transportFeeData) return null;

  const amountDue = parseFloat(transportFeeData.amountDue || 0);
  const amountPaid = parseFloat(transportFeeData.amountPaid || 0);
  const balance = parseFloat(transportFeeData.balance || 0);

  const getStatusColor = (status) => {
    const colors = {
      Pending: "orange",
      "Partially Paid": "blue",
      "Fully Paid": "green",
      Overdue: "red",
    };
    return colors[status] || "default";
  };

  // Payment records table (if available)
  const paymentColumns = [
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (date) => dayjs(date).format("YYYY-MM-DD HH:mm"),
      width: 150,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => FinanceService.formatCurrency(amount),
      align: "right",
      width: 120,
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (method) => method || "-",
      width: 120,
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      render: (ref) => ref || "-",
      width: 150,
    },
  ];

  const paymentRecords = transportFeeData.payments || [];

  return (
    <Drawer
      title="Transport Fee Details"
      placement="right"
      onClose={onClose}
      open={visible}
      width={900}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <Spin spinning={loading}>
        {/* Transport Fee Information */}
        <Descriptions
          title="Transport Fee Information"
          bordered
          column={2}
          size="small"
          style={{ marginBottom: 24 }}
        >
          <Descriptions.Item label="Student" span={2}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {transportFeeData.firstName} {transportFeeData.lastName}
              </div>
              <div style={{ fontSize: "12px", color: "#888" }}>
                SPC:{" "}
                {transportFeeData.spc ||
                  transportFeeData.studentSpc ||
                  transportFeeData.spcCode}
              </div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Route">
            <Tag color="blue">{transportFeeData.routeName}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Transport Type">
            {transportFeeData.transportTypeName}
          </Descriptions.Item>
          <Descriptions.Item label="TPC">
            {transportFeeData.tpc}
          </Descriptions.Item>
          <Descriptions.Item label="Term">
            {transportFeeData.termName || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(transportFeeData.status)}>
              {transportFeeData.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created Date">
            {dayjs(transportFeeData.createdAt).format("YYYY-MM-DD HH:mm")}
          </Descriptions.Item>
          <Descriptions.Item label="Updated Date">
            {dayjs(transportFeeData.updatedAt).format("YYYY-MM-DD HH:mm")}
          </Descriptions.Item>
        </Descriptions>

        {/* Payment Information Card */}
        <Divider />
        <div style={{ marginBottom: 24 }}>
          <h4>Payment Summary</h4>
          <Row gutter={16}>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title="Amount Due"
                  value={amountDue}
                  precision={2}
                  valueStyle={{ color: "#1890ff" }}
                  formatter={(value) => FinanceService.formatCurrency(value)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title="Amount Paid"
                  value={amountPaid}
                  precision={2}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: "#52c41a" }}
                  formatter={(value) => FinanceService.formatCurrency(value)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title="Balance"
                  value={balance}
                  precision={2}
                  prefix={
                    balance > 0 ? <WarningOutlined /> : <CheckCircleOutlined />
                  }
                  valueStyle={{ color: balance > 0 ? "#f5222d" : "#52c41a" }}
                  formatter={(value) => FinanceService.formatCurrency(value)}
                />
              </Card>
            </Col>
          </Row>
        </div>

        {/* Payment Records */}
        {paymentRecords.length > 0 && (
          <>
            <Divider />
            <div style={{ marginBottom: 24 }}>
              <h4>Payment Records ({paymentRecords.length})</h4>
              <Table
                columns={paymentColumns}
                dataSource={paymentRecords}
                rowKey={(record) => record.paymentId || record.id}
                pagination={false}
                size="small"
                scroll={{ x: 600 }}
              />
            </div>
          </>
        )}
      </Spin>
    </Drawer>
  );
};

export default TransportFeeDetailDrawer;
